/**
 * Sélecteur de style visuel de l'interface (Clair/Sombre aujourd'hui, autres
 * thèmes à venir — voir UI_THEMES). Visible des visiteurs anonymes (header)
 * et dans les paramètres de compte, sur le même modèle que LanguageSwitcher.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "next-themes";
import { SunMoon, Check, ChevronDown } from "lucide-react";
import { UI_THEMES, type UiThemeId, isUiThemeId } from "../lib/uiThemes";
import { cn } from "../lib/utils";
import { useAuth } from "../contexts/AuthContext";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "./ui/dropdown-menu";

export interface ThemePickerProps {
  className?: string;
}

export function ThemePicker({ className }: ThemePickerProps) {
  const { t } = useTranslation("common");
  const { user, updateProfile } = useAuth();
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  // next-themes ne résout `theme` qu'après montage (même précaution que
  // l'ancien ThemeToggle) — évite un état incohérent au tout premier rendu.
  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    return null;
  }

  const current: UiThemeId = theme && isUiThemeId(theme) ? theme : "coral";

  const changeTheme = (id: UiThemeId) => {
    setTheme(id);
    if (user) void updateProfile({ ui_theme: id });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={t("theme_picker.change", {
          current: t(`theme_picker.themes.${current}`),
        })}
        className={cn(
          "inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring rounded",
          className,
        )}
        data-testid="theme-picker-trigger"
      >
        <SunMoon className="w-4 h-4" />
        <span className="hidden sm:inline">
          {t(`theme_picker.themes.${current}`)}
        </span>
        <ChevronDown className="w-3.5 h-3.5 opacity-70 hidden sm:inline" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[9rem]">
        {UI_THEMES.map((id) => (
          <DropdownMenuItem
            key={id}
            onSelect={() => changeTheme(id)}
            className="flex items-center justify-between gap-2 cursor-pointer"
            data-testid={`theme-picker-option-${id}`}
          >
            <span>{t(`theme_picker.themes.${id}`)}</span>
            {current === id && <Check className="w-4 h-4 text-primary" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default ThemePicker;
